import {
  Users,
  DollarSign,
  Server,
  AlertCircle,
  Clock,
  CheckCircle2,
  Globe,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/empty-state";
import { useAdminDashboard } from "@/hooks/dashboard/use-admin-dashboard";
import { KpiCard } from "./kpi-card";
import { RevenueTrendChart } from "./revenue-trend-chart";

function formatCurrency(amount: number) {
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function personName(user: {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email;
}

/**
 * A pending row now covers three provisioning states, not one. Rendering them
 * all as "Pending deployment" would hide the two that need attention.
 */
const PENDING_LABEL: Record<string, string> = {
  NONE: "Awaiting setup",
  PENDING_DEPLOYMENT: "Pending deployment",
  PROVISIONING: "Provisioning",
  PROVISION_FAILED: "Provision failed",
};

const PENDING_BADGE: Record<string, string> = {
  NONE: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300",
  PENDING_DEPLOYMENT:
    "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300",
  PROVISIONING:
    "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300",
  PROVISION_FAILED:
    "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300",
};

export function AdminDashboard() {
  const { data, isLoading, error } = useAdminDashboard();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
        <Skeleton className="h-80 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load dashboard data.</AlertDescription>
      </Alert>
    );
  }

  const { kpis, pendingDeployments, downDeployments, revenueTrend } = data;
  const deploymentsDown = kpis.deployments.total - kpis.deployments.up;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Platform overview and operational health
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          title="Total Users"
          value={kpis.users.total.toLocaleString()}
          icon={Users}
          changePct={kpis.users.changePct}
          subValue={`${kpis.users.last30Days.toLocaleString()} in last 30 days`}
          iconClassName="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
        />
        <KpiCard
          title="Total Revenue"
          value={formatCurrency(kpis.revenue.totalInCents)}
          icon={DollarSign}
          changePct={kpis.revenue.changePct}
          subValue={`${formatCurrency(kpis.revenue.last30DaysInCents)} in last 30 days`}
          iconClassName="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
        />
        <KpiCard
          title="Deployments Up"
          value={`${kpis.deployments.up} / ${kpis.deployments.total}`}
          icon={Server}
          subValue={
            kpis.deployments.failed > 0
              ? `${kpis.deployments.failed} failed to provision`
              : kpis.deployments.provisioning > 0
                ? `${kpis.deployments.provisioning} provisioning now`
                : deploymentsDown > 0
                  ? `${deploymentsDown} currently down`
                  : "All systems operational"
          }
          changeLabel={`${kpis.deployments.total} total deployments`}
          iconClassName="bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400"
        />
      </div>

      {/* Revenue trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Revenue trend</CardTitle>
        </CardHeader>
        <CardContent>
          {revenueTrend.some((r) => r.totalInCents > 0) ? (
            <RevenueTrendChart data={revenueTrend} />
          ) : (
            <EmptyState
              icon={DollarSign}
              title="No revenue yet"
              description="Successful payments will appear here as a monthly trend."
            />
          )}
        </CardContent>
      </Card>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending deployments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Pending deployments
              <Badge variant="secondary" className="ml-auto">
                {pendingDeployments.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingDeployments.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="Nothing pending"
                description="All subscriptions have a provisioned deployment."
              />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {pendingDeployments.map((s) => (
                  <li
                    key={s.subscriptionId}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {s.company?.name ?? "—"}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {s.user ? personName(s.user) : "—"}
                        {s.plan ? ` · ${s.plan.displayName}` : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge
                        variant="outline"
                        className={
                          PENDING_BADGE[s.deploymentStatus ?? "NONE"] ??
                          PENDING_BADGE.NONE
                        }
                      >
                        {PENDING_LABEL[s.deploymentStatus ?? "NONE"] ??
                          "Awaiting setup"}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDistanceToNow(new Date(s.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Down deployments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              Deployments down
              <Badge variant="secondary" className="ml-auto">
                {downDeployments.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {downDeployments.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="All deployments are up"
                description="Every live deployment reported a recent heartbeat."
              />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {downDeployments.map((d) => (
                  <li
                    key={d.deploymentId}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {d.company?.name ?? "—"}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-1">
                        <Globe className="h-3 w-3 shrink-0" />
                        {d.frontendDomain}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge
                        variant="outline"
                        className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300"
                      >
                        DOWN
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        {d.lastHeartbeatAt
                          ? formatDistanceToNow(new Date(d.lastHeartbeatAt), {
                              addSuffix: true,
                            })
                          : "No heartbeat"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
